import React from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import ListCard from "../../common/ListCard";
import LinkedText from "../../common/LinkedText";

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 16px;
`;

const Title = styled.h1`
  font-size: 1.6em;
  margin: 4px 0 6px;
  color: #1a1a1a;
  overflow-wrap: anywhere;
`;

const DateLine = styled.div`
  color: var(--color-text-secondary, #666);
  font-size: 0.9em;
  margin: 0 0 14px;
`;

const Prose = styled.div`
  p {
    line-height: 1.55;
    color: #333;
    margin: 0 0 14px;
    white-space: pre-line;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.15em;
  margin: 24px 0 12px;
`;

const EndedBanner = styled.div`
  background-color: #ff9800;
  color: white;
  padding: 12px;
  border-radius: 4px;
  margin: 16px 0;
  font-weight: bold;
  text-align: center;
`;

const formatDateRange = (start, end) => {
  if (!start && !end) return null;
  const opts = { month: "long", day: "numeric", year: "numeric" };
  const s = start ? new Date(start).toLocaleDateString(undefined, opts) : null;
  const e = end ? new Date(end).toLocaleDateString(undefined, opts) : null;
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e;
};

const EventOverviewPanel = ({ event, hasEnded }) => {
  const [searchParams] = useSearchParams();
  const mapParams = new URLSearchParams(searchParams);
  mapParams.delete("view");
  const mapSearch = mapParams.toString();
  const mapHref = mapSearch ? `?${mapSearch}` : `?`;

  const dateRange = formatDateRange(event.startDate, event.endDate);
  const proseParagraphs = (event.description || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <Container>
      <Title>{event.name}</Title>
      {dateRange && <DateLine>{dateRange}</DateLine>}
      {proseParagraphs.length > 0 && (
        <Prose>
          {proseParagraphs.map((p, i) => (
            <p key={i}>
              <LinkedText text={p} />
            </p>
          ))}
        </Prose>
      )}
      {hasEnded && (
        <EndedBanner>
          This event has ended, but you can still explore the restaurants that
          participated!
        </EndedBanner>
      )}
      <SectionHeading>Explore further</SectionHeading>
      <ListCard
        to={mapHref}
        title="See participating restaurants on the map"
        description={`Browse every vendor at ${event.name} with location, hours, and menu highlights.`}
        icon="fas fa-map"
        iconLabel="Map view"
      />
    </Container>
  );
};

EventOverviewPanel.propTypes = {
  event: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  hasEnded: PropTypes.bool,
};

export default EventOverviewPanel;
